/**
 * ¿Este bucket es público? — carril web. (#568)
 *
 * Mismo contrato que `mcp-server/src/utils/storageBucketPublicity.js` (leelo:
 * ahí está el porqué completo de no hardcodear una lista de buckets), con una
 * diferencia MEDIDA que no se puede tapar:
 *
 * **El navegador no puede preguntar.** `GET /storage/v1/bucket/:id` no está
 * permitido para `anon` ni para `authenticated`: contra el stack local, con un
 * JWT real de admin, devuelve `400 {"statusCode":"404","error":"Bucket not
 * found"}` tanto para `inventory-images` (privado) como para `avatars`
 * (público). Sólo la service role lo contesta, y la service role no vive en el
 * navegador — ni debe.
 *
 * Igual se INTENTA `getBucket()` en vez de asumir: si algún día una política de
 * storage habilita esa lectura, este carril se cura solo sin tocar código. Hoy
 * falla, y el fallback es **privado → firmar**, que es el lado seguro:
 * `createSignedUrl` contra un bucket PÚBLICO funciona (medido: 200, y el GET
 * del enlace firmado también 200), mientras que asumir público contra uno
 * privado devuelve el enlace muerto de #565.
 */
import { supabase } from '../services/supabase';

/** bucket → boolean. Memoizado por sesión del navegador. */
const cache = new Map();

/**
 * @param {string} bucket
 * @returns {Promise<boolean>} true sólo si el servidor de storage confirma que
 *   el bucket es público. Ante cualquier error: false (firmar).
 */
export const isBucketPublic = async (bucket) => {
  if (!bucket) return false;
  if (cache.has(bucket)) return cache.get(bucket);

  let result = false;
  try {
    const { data, error } = await supabase.storage.getBucket(bucket);
    if (error || !data) {
      // Esperado en el navegador (ver cabecera). Se cachea igual: reintentar
      // por cada imagen de cada listado sería un round-trip por miniatura.
      cache.set(bucket, false);
      return false;
    }
    result = !!data.public;
  } catch {
    cache.set(bucket, false);
    return false;
  }

  cache.set(bucket, result);
  return result;
};

/** Sólo para tests: olvida lo memoizado. */
export const resetBucketPublicityCache = () => cache.clear();

const api = { isBucketPublic, resetBucketPublicityCache };
export default api;
